import { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import he from 'he';
import { marked } from 'marked';

interface SafeHTMLProps {
  html: string;
  className?: string;
  style?: React.CSSProperties;
}

const SafeHTML = ({ html, className, style }: SafeHTMLProps) => {
  const [sanitizedHTML, setSanitizedHTML] = useState('');

  useEffect(() => {
    const convert = async () => {
      // Step 1: Decode any HTML entities (e.g., &amp; -> &)
      const decoded = he.decode(html);

      // Step 2: Replace \n with <br /> for visible line breaks
      const withLineBreaks = decoded.replace(/\n/g, '<br />');

      // Step 3: Convert any markdown syntax to inline HTML
      const markdownConverted = await marked.parseInline(withLineBreaks);

      // Step 4: Sanitize the HTML to avoid XSS
      const config = {
        ALLOWED_TAGS: [
          'a', 'abbr', 'acronym', 'address', 'area', 'article', 'aside', 'audio',
          'b', 'bdi', 'bdo', 'big', 'blockquote', 'br', 'button',
          'caption', 'center', 'cite', 'code', 'col', 'colgroup',
          'data', 'datalist', 'dd', 'del', 'details', 'dfn', 'div', 'dl', 'dt',
          'em', 'fieldset', 'figcaption', 'figure', 'font', 'footer', 'form',
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header', 'hr', 'i', 'img', 'input',
          'ins', 'kbd', 'label', 'legend', 'li', 'main', 'map', 'mark', 'menu', 'meter',
          'nav', 'ol', 'optgroup', 'option', 'output', 'p', 'pre', 'progress', 'q',
          'rp', 'rt', 'ruby', 's', 'samp', 'section', 'select', 'small', 'source',
          'span', 'strike', 'strong', 'sub', 'summary', 'sup', 'svg',
          'table', 'tbody', 'td', 'textarea', 'tfoot', 'th', 'thead', 'time', 'tr',
          'track', 'tt', 'u', 'ul', 'var', 'video', 'wbr', 'iframe'
        ],
        ALLOWED_ATTR: [
          'abbr', 'accept', 'accept-charset', 'accesskey', 'action', 'align', 'alt',
          'aria-*', 'autocapitalize', 'autocomplete', 'autofocus', 'autoplay',
          'background', 'bgcolor', 'border', 'cellpadding', 'cellspacing', 'charset',
          'checked', 'cite', 'class', 'color', 'cols', 'colspan', 'content',
          'contenteditable', 'controls', 'coords', 'data-*', 'datetime', 'default',
          'dir', 'dirname', 'disabled', 'download', 'draggable', 'enctype', 'for',
          'form', 'headers', 'height', 'hidden', 'high', 'href', 'hreflang', 'id',
          'inputmode', 'ismap', 'kind', 'label', 'lang', 'list', 'loop', 'low',
          'max', 'maxlength', 'media', 'method', 'min', 'multiple', 'muted', 'name',
          'novalidate', 'open', 'optimum', 'pattern', 'placeholder', 'poster',
          'preload', 'readonly', 'rel', 'required', 'reversed', 'role', 'rows',
          'rowspan', 'sandbox', 'scope', 'selected', 'shape', 'size', 'sizes',
          'span', 'spellcheck', 'src', 'srcdoc', 'srclang', 'start', 'step', 'style',
          'tabindex', 'target', 'title', 'translate', 'type', 'usemap', 'value',
          'width', 'wrap', 'loading', 'allow', 'allowfullscreen', 'frameborder'
        ],
        FORBID_TAGS: ['script', 'object', 'embed', 'form'],
        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus']
      } as unknown as DOMPurify.Config;

      const sanitized = DOMPurify.sanitize(markdownConverted, config);
      // const sanitized = DOMPurify.sanitize(markdownConverted, {
      //   ALLOWED_ATTR: [
      //     'href', 'target', 'rel', 'controls', 'src', 'type',
      //     'width', 'height', 'class', 'style', 'id', 'data-*',
      //     'aria-*', 'role', 'title', 'alt', 'lang', 'dir', 'tabindex'
      //   ],
      // });

      // Step 5: Set the sanitized HTML into state
      setSanitizedHTML(sanitized);
    };

    convert();
  }, [html]);

  return (
    <div
      className={className}
      style={style}
      dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
    />
  );
};

export default SafeHTML;